import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Package, EnvelopeSimple, Lock, Eye, EyeSlash, User, Translate } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t, language, toggleLanguage, langLabel , tx } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password || !confirmPassword) {
      toast.error(tx('Please fill in all fields', 'Por favor completa todos los campos', 'Please fill in all fields'));
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error(tx('Passwords do not match', 'Las contraseñas no coinciden', 'Passwords do not match'));
      return;
    }
    
    if (password.length < 6) {
      toast.error(tx('Password must be at least 6 characters', 'La contraseña debe tener al menos 6 caracteres', 'Password must be at least 6 characters'));
      return;
    }
    
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success(tx('Account created successfully!', '¡Cuenta creada exitosamente!', 'Account created successfully!'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || (tx('Registration failed', 'Error al crear cuenta', 'Registration failed')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 ship-gradient relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFD54F] rounded-full -translate-y-1/2 translate-x-1/2 opacity-30" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FFB300] rounded-full translate-y-1/2 -translate-x-1/2 opacity-20" />
        
        <div className="relative z-10 flex flex-col items-center justify-center w-full p-12">
          <div className="mb-8">
            <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="h-44 mx-auto mb-4" />
            <p className="text-white/80 text-center mt-2">
              Shipping & Logistics Solutions
            </p>
          </div>

          <div className="text-white/90 text-center space-y-4 max-w-sm">
            <p className="text-lg">
              {tx('Professional courier management system', 'Sistema de gestión de courier profesional', 'Professional courier management system')}
            </p>
          </div>

          <div className="absolute bottom-8 text-white/60 text-sm text-center">
            <p>trackmypackage.app</p>
            <p>info@trackmypackage.app</p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="flex justify-end mb-6">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Translate size={18} />
              <span>{langLabel}</span>
            </button>
          </div>

          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="h-20" />
          </div>

          <h1 
            className="text-3xl font-bold text-gray-900 tracking-wide mb-2"
            style={{ fontFamily: 'Bebas Neue' }}
          >
            {t('createAccount')}
          </h1>
          <p className="text-gray-600 mb-8">
            {tx('Create your account to get started', 'Crea tu cuenta para comenzar', 'Create your account to get started')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="form-label">{t('fullName')}</Label>
              <div className="relative">
                <User size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('enterName')}
                  className="pl-10 rounded-lg"
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="form-label">{t('email')}</Label>
              <div className="relative">
                <EnvelopeSimple size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('enterEmail')}
                  className="pl-10 rounded-lg"
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="form-label">{t('password')}</Label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('enterPassword')}
                  className="pl-10 pr-10 rounded-lg"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="form-label">
                {tx('Confirm Password', 'Confirmar Contraseña', 'Confirm Password')}
              </Label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={tx('Confirm your password', 'Confirma tu contraseña', 'Confirm your password')}
                  className="pl-10 rounded-lg"
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full ship-gradient hover:opacity-90 rounded-lg font-semibold text-white py-3"
              data-testid="register-submit-btn"
            >
              {loading ? (tx('Creating...', 'Creando...', 'Creating...')) : t('createAccountBtn')}
            </Button>
          </form>

          <p className="mt-6 text-center text-gray-600">
            {t('alreadyHaveAccount')}{' '}
            <Link 
              to="/login" 
              className="text-[#0077FF] font-semibold hover:underline"
              data-testid="login-link"
            >
              {t('signIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
